/*
 * Cortex consent banner: an own front end for Shopify's Customer Privacy API.
 * Base-owned, loaded with defer by snippets/cortex-consent-banner.liquid, and
 * only when the theme setting consent_banner is on (Shopify's own banner must
 * then be off in the shop, docs/TRACKING.md).
 *
 * It records nothing itself. The answer goes to
 * Shopify.customerPrivacy.setTrackingConsent, so Shopify, the checkout web
 * pixel and this theme read one answer; cortex-consent turns the resulting
 * visitorConsentCollected into 'cortex:consent' as before.
 *
 * - Opens by itself only when shouldShowBanner() is true (the region requires
 *   consent) and currentVisitorConsent() holds no answer yet. Both are needed:
 *   measured on STAUS&BACH 13-10-2026, shouldShowBanner() stays true after an
 *   answer, also on the next page. Not in the theme editor.
 * - The footer button "Cookie-instellingen" calls CortexConsentBanner.open(),
 *   which shows the visitor's current choice, so consent can be withdrawn.
 * - Weigeren: everything off. Accepteren: everything on. As soon as a switch
 *   differs from where it started, Accepteren reads "Keuze opslaan" and saves
 *   exactly the switches, so a partial choice is never turned into "all".
 * - sale_of_data follows marketing; necessary cookies are not a choice.
 * - "Voorkeuren beheren" ([data-consent-more], aria-expanded) unfolds the
 *   shop's explanation under every switch ([data-consent-detail]); every
 *   opening starts folded.
 * - Closing it dispatches document event 'cortex:consent-banner:closed', so a
 *   popup can wait for it (cortex-newsletter does).
 *
 * The pure rules are on window.CortexConsentBanner for tools/test.
 */
(() => {
  if (window.CortexConsentBanner) return;

  const CATEGORIES = ['analytics', 'marketing', 'preferences'];
  const ALLOWED = {
    analytics: 'analyticsProcessingAllowed',
    marketing: 'marketingAllowed',
    preferences: 'preferencesProcessingAllowed',
  };

  const choiceOf = (value) => Object.fromEntries(CATEGORIES.map((key) => [key, value]));

  /** The visitor's stored answer per category ('yes', 'no' or ''), or null. */
  function given(privacy) {
    try {
      return typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent() : null;
    } catch (error) {
      return null;
    }
  }

  const answered = (consent) =>
    !!consent && [...CATEGORIES, 'sale_of_data'].some((key) => consent[key] === 'yes' || consent[key] === 'no');

  /** True only when the region requires consent and this visitor has not answered. */
  function shouldOpen(privacy) {
    if (!privacy || typeof privacy.shouldShowBanner !== 'function') return false;
    try {
      return privacy.shouldShowBanner() === true && !answered(given(privacy));
    } catch (error) {
      return false;
    }
  }

  /** The switches as setTrackingConsent wants them; sale_of_data follows marketing. */
  function toApi(choice) {
    const consent = Object.fromEntries(CATEGORIES.map((key) => [key, choice[key] === true]));
    consent.sale_of_data = consent.marketing;
    return consent;
  }

  /**
   * What the switches show when the banner opens: the visitor's own answer per
   * category, and where there is none, what Shopify allows right now (false in
   * a consent region, the region default elsewhere). No API: everything off.
   */
  function currentChoice(privacy) {
    if (!privacy) return choiceOf(false);
    const stored = given(privacy);
    return Object.fromEntries(
      CATEGORIES.map((key) => {
        const answer = stored && stored[key];
        if (answer === 'yes' || answer === 'no') return [key, answer === 'yes'];
        try {
          return [key, typeof privacy[ALLOWED[key]] === 'function' && privacy[ALLOWED[key]]() === true];
        } catch (error) {
          return [key, false];
        }
      }),
    );
  }

  const differs = (a, b) => CATEGORIES.some((key) => !!a[key] !== !!b[key]);

  /** 'save' once a switch moved away from where it started, else 'accept'. */
  function acceptMode(initial, switches) {
    return differs(initial, switches) ? 'save' : 'accept';
  }

  /** The choice a button stands for. */
  function choiceFor(action, switches, initial) {
    if (action === 'reject') return choiceOf(false);
    if (acceptMode(initial, switches) === 'accept') return choiceOf(true);
    return Object.fromEntries(CATEGORIES.map((key) => [key, switches[key] === true]));
  }

  const rules = { CATEGORIES, shouldOpen, toApi, currentChoice, acceptMode, choiceFor };
  window.CortexConsentBanner = { ...rules, open: () => false };

  if (typeof document === 'undefined') return;
  const root = document.querySelector('[data-consent-banner]');
  if (!root) return;

  const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
  const switches = [...root.querySelectorAll('[data-consent-category]')];
  const buttons = [...root.querySelectorAll('[data-consent-action]')];
  const acceptButton = root.querySelector('[data-consent-action="accept"]');
  const error = root.querySelector('[data-consent-error]');
  const more = root.querySelector('[data-consent-more]');
  const details = [...root.querySelectorAll('[data-consent-detail]')];
  let initial = choiceOf(false);
  let opener = null;
  let reopened = false;

  const readSwitches = () =>
    Object.fromEntries(switches.map((input) => [input.dataset.consentCategory, input.checked]));

  function showMode() {
    const save = acceptMode(initial, readSwitches()) === 'save';
    acceptButton.textContent = save ? acceptButton.dataset.labelSave : acceptButton.dataset.labelAccept;
  }

  function unfold(open) {
    if (!more) return;
    more.setAttribute('aria-expanded', String(open));
    details.forEach((detail) => {
      detail.hidden = !open;
    });
    root.toggleAttribute('data-expanded', open);
  }

  function close() {
    if (root.hidden) return;
    root.hidden = true;
    if (opener && typeof opener.focus === 'function') opener.focus();
    opener = null;
    document.dispatchEvent(new CustomEvent('cortex:consent-banner:closed'));
  }

  function open(from) {
    const api = privacy();
    if (!api || typeof api.setTrackingConsent !== 'function') return false;
    initial = currentChoice(api);
    switches.forEach((input) => {
      input.checked = initial[input.dataset.consentCategory] === true;
    });
    error.hidden = true;
    unfold(false);
    showMode();
    reopened = !!from;
    opener = from || null;
    root.hidden = false;
    // Focus moves only into a banner the visitor opened; the first one never
    // takes focus from the page, it is early in the tab order instead.
    if (reopened) root.querySelector('[data-consent-focus]').focus();
    return true;
  }

  function save(action) {
    const api = privacy();
    if (!api || typeof api.setTrackingConsent !== 'function') return;
    const choice = choiceFor(action, readSwitches(), initial);
    buttons.forEach((button) => {
      button.disabled = true;
    });
    root.setAttribute('aria-busy', 'true');
    const done = (result) => {
      buttons.forEach((button) => {
        button.disabled = false;
      });
      root.removeAttribute('aria-busy');
      if (result && result.error) {
        error.hidden = false;
        return;
      }
      close();
    };
    try {
      api.setTrackingConsent(toApi(choice), done);
    } catch (failure) {
      done({ error: failure });
    }
  }

  switches.forEach((input) => input.addEventListener('change', showMode));
  if (more) more.addEventListener('click', () => unfold(more.getAttribute('aria-expanded') !== 'true'));
  buttons.forEach((button) => button.addEventListener('click', () => save(button.dataset.consentAction)));
  root.addEventListener('keydown', (event) => {
    // Escape only closes a banner the visitor opened; the first one waits for a choice.
    if (event.key === 'Escape' && reopened) {
      event.preventDefault();
      close();
    }
  });
  // An answer given elsewhere (Shopify's own banner while it is still on)
  // makes the question moot.
  document.addEventListener('visitorConsentCollected', () => {
    if (!reopened) close();
  });

  window.CortexConsentBanner = { ...rules, open };

  if (root.hasAttribute('data-design-mode')) return;
  // Capture before legacy footer handlers so they cannot open a second banner.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-standard-link="cookies"], #cookiePrefs, [data-cortex-cookie-open], a[href="#shopifyReshowConsentBanner"]');
    if (!button || !open(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  const showIfNeeded = () => {
    if (root.hidden && shouldOpen(privacy())) open(null);
  };
  const gate = window.CortexConsent;
  if (gate && typeof gate.ready === 'function') gate.ready().then(showIfNeeded);
  else if (privacy()) showIfNeeded();
  else {
    const load = () => {
      if (privacy()) return showIfNeeded();
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (!error) showIfNeeded();
        });
      }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', load, { once: true });
    else load();
  }
})();
